import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { publisherApi } from '../api';
import { ACADEMIA_GROUPS } from '../kindSchemas';
import type { PublisherFormDraft } from '../PublisherHub';
import type { AttachmentItem, ContentCreatePayload } from '../types';
import { validateHashtagCount } from './HashtagEditor';
import { PublisherGroups } from './PublisherGroups';
import { PublisherPreview } from './PublisherPreview';

interface AcademiaPublisherProps {
  onSuccess: (message?: string) => void;
  lockedKind?: string;
  initialDraft?: PublisherFormDraft;
  onDraftChange?: (patch: PublisherFormDraft) => void;
}

function draftValues(draft?: PublisherFormDraft): Record<string, string> {
  return {
    title: draft?.title || '',
    summary: draft?.summary || '',
    body: draft?.body || '',
    pollOptions: (draft?.pollOptions || []).join('\n'),
    ...(draft?.meta || {}),
  };
}

export const AcademiaPublisher = ({
  onSuccess,
  lockedKind,
  initialDraft,
  onDraftChange,
}: AcademiaPublisherProps) => {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const { notify } = useToast();
  const isOwner = hasRole('SYSTEM_OWNER');
  const [kind, setKind] = useState(lockedKind || 'NEWS');
  const [values, setValues] = useState<Record<string, string>>(() => draftValues(initialDraft));
  const [hashtags, setHashtags] = useState<string[]>(initialDraft?.hashtags || []);
  const [fileMap, setFileMap] = useState<Record<string, AttachmentItem[]>>({});
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const sending = useRef(false);
  const handoffCreated = useRef(false);
  const savedContent = useRef<{ id: string; payload: string } | null>(null);

  useEffect(() => {
    if (lockedKind) setKind(lockedKind);
  }, [lockedKind]);

  const groups = useMemo(() => ACADEMIA_GROUPS[kind] || ACADEMIA_GROUPS.NEWS, [kind]);

  const collectedFiles = (map = fileMap) => Object.values(map).flat();

  const emit = (
    nextValues: Record<string, string>,
    nextTags = hashtags,
    nextFiles = collectedFiles()
  ) => {
    onDraftChange?.({
      title: nextValues.title,
      summary: nextValues.summary,
      body: nextValues.body,
      hashtags: nextTags,
      pollOptions: String(nextValues.pollOptions || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean),
      officialUrl: nextValues.sourceUrl,
      attachments: nextFiles,
      meta: nextValues,
    });
  };

  const updateValue = (name: string, value: string) => {
    setValues((prev) => {
      const next = { ...prev, [name]: value };
      emit(next);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent, isDraft: boolean) => {
    e.preventDefault();
    if (sending.current) return;
    const title = (values.title || '').trim();
    const summary = (values.summary || values.body || title).trim();
    if (!title || (!isDraft && kind !== 'QUICK_POLL' && !summary)) {
      notify('Заполните заголовок и краткое описание');
      return;
    }
    if (!isDraft && validateHashtagCount(hashtags)) {
      notify(t('publisher.validation.tags.min' as any));
      return;
    }
    const pollOptions = String(values.pollOptions || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (!isDraft && kind === 'QUICK_POLL' && pollOptions.length < 2) {
      notify('Для быстрого опроса нужно минимум 2 варианта');
      return;
    }

    sending.current = true;
    setLoading(true);
    setSubmitError('');
    try {
      const payload: ContentCreatePayload = {
        kind,
        subtype: values.subtype || undefined,
        title,
        summary: summary || title,
        body: values.body || undefined,
        organization: values.organization || undefined,
        category: values.category || undefined,
        official_url: values.sourceUrl || undefined,
        language: values.language || undefined,
        audience: values.audienceMode || 'ALL',
        event_start: values.eventDate || undefined,
        event_end: values.endDate || undefined,
        deadline: values.deadline || undefined,
        feed_eligible: true,
        is_important: values.important === '1',
        is_promoted: values.promoted === '1',
        cover_file_id: fileMap.cover?.[0]?.file_id,
        attachments: collectedFiles(),
        hashtags,
        poll_options: kind === 'QUICK_POLL' ? pollOptions : undefined,
        poll_allow_multiple: values.multi === '1',
        poll_show_before_vote: values.reveal === '1',
        meta: {
          format_type: values.format || undefined,
          related_object_id: values.linkedObject || undefined,
          achievement_type: kind === 'ACHIEVEMENT' ? values.subtype : undefined,
        },
      };
      const serialized = JSON.stringify(payload);
      const saved = savedContent.current;
      const res = saved
        ? saved.payload === serialized
          ? saved
          : await publisherApi.updateContent(saved.id, payload)
        : await publisherApi.createContent(payload);
      savedContent.current = { id: res.id, payload: serialized };
      if (!isDraft && kind === 'ACHIEVEMENT' && values.socialAlso === '1' && !handoffCreated.current) {
        await publisherApi.createHandoff({
          source_content_id: res.id,
          material_type: values.subtype || 'Достижение',
          title,
          description: summary,
          channels: 'Instagram,сайт ENU',
          hashtags,
        });
        handoffCreated.current = true;
      }
      if (!isDraft) {
        if (isOwner) {
          await publisherApi.publishContent(res.id);
          onSuccess(t('publisher.toast.published' as any));
        } else {
          await publisherApi.submitForModeration(res.id);
          onSuccess(t('publisher.toast.submitted' as any));
        }
      } else {
        onSuccess(t('publisher.toast.draftSaved' as any));
      }
    } catch {
      setSubmitError(savedContent.current
        ? 'Материал сохранён, но отправку завершить не удалось. Повторите попытку или вернитесь к редактированию.'
        : 'Не удалось сохранить материал. Проверьте соединение и повторите попытку. Введённые данные остались в форме.');
    } finally {
      sending.current = false;
      setLoading(false);
    }
  };

  const openPreview = (event: React.FormEvent) => {
    event.preventDefault();
    const title = (values.title || '').trim();
    const summary = (values.summary || values.body || title).trim();
    if (!title || (kind !== 'QUICK_POLL' && !summary)) {
      notify('Заполните заголовок и краткое описание');
      return;
    }
    if (validateHashtagCount(hashtags)) {
      notify(t('publisher.validation.tags.min' as any));
      return;
    }
    const pollOptions = String(values.pollOptions || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (kind === 'QUICK_POLL' && pollOptions.length < 2) {
      notify('Для быстрого опроса нужно минимум 2 варианта');
      return;
    }
    setSubmitError('');
    setPreviewing(true);
  };

  if (previewing) {
    return (
      <PublisherPreview
        badge={t(`publisher.kind.${kind}` as any) || kind}
        title={(values.title || '').trim()}
        summary={(values.summary || values.body || '').trim()}
        sourceUrl={values.sourceUrl}
        tags={hashtags}
        attachments={collectedFiles()}
        rows={[
          ['Категория', values.category],
          ['Тип', values.subtype],
          ['Аудитория', values.audienceMode],
        ].filter((row): row is [string, string] => Boolean(row[1]))}
        confirmLabel={
          isOwner ? t('publisher.action.publishNow' as any) : t('publisher.action.submitModeration' as any)
        }
        busy={loading}
        error={submitError}
        onEdit={() => setPreviewing(false)}
        onConfirm={() => {
          void handleSubmit({ preventDefault() {} } as React.FormEvent, false);
        }}
      />
    );
  }

  return (
    <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }} onSubmit={openPreview}>
      <div className="rg-chip-row">
        <span className="rg-chip is-amber">{t(`publisher.kind.${kind}` as any) || kind}</span>
        <span className="rg-chip">Лента Academia</span>
      </div>
      <PublisherGroups
        groups={groups}
        values={values}
        onChange={updateValue}
        hashtags={hashtags}
        files={fileMap}
        onFiles={(name, next) => {
          setFileMap((prev) => {
            const map = { ...prev, [name]: next };
            emit(values, hashtags, Object.values(map).flat());
            return map;
          });
        }}
        onHashtags={(next) => {
          setHashtags(next);
          emit(values, next);
        }}
      />
      {submitError ? <p role="alert" className="rg-publisher-error">{submitError}</p> : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <button type="button" onClick={(e) => handleSubmit(e, true)} disabled={loading} className="rg-btn">
          {t('publisher.action.saveDraft' as any)}
        </button>
        <button type="submit" disabled={loading} className="rg-btn primary">
          Предпросмотр →
        </button>
      </div>
    </form>
  );
};
